import { useState, useCallback, useMemo } from 'react';

/**
 * Custom hook for managing AG Grid column visibility state
 *
 * @param {Array} columns - Array of column definitions
 * @returns {Object} - { columns: enhancedColumns, onColumnVisible: callback }
 *
 * @example
 * const baseColumns = [
 *   { headerName: 'Name', field: 'name' },
 *   { headerName: 'Email', field: 'email', hide: true }
 * ];
 *
 * const { columns, onColumnVisible } = useColumnState(baseColumns);
 *
 * <AGGridMain
 *   tableData={{ columns, data }}
 *   onColumnVisible={onColumnVisible}
 * />
 */
export const useColumnState = (baseColumns) => {
    // State to track column visibility changes
    const [columnState, setColumnState] = useState({});

    // Callback to handle column visibility changes from AG Grid
    const onColumnVisible = useCallback((params) => {
        if (params.column) {
            const colId = params.column.getColId();
            const isVisible = params.visible;
            setColumnState((prev) => ({
                ...prev,
                [colId]: { hide: !isVisible },
            }));
        }
    }, []);

    // Enhance columns with current visibility state - memoized to prevent infinite loops
    const enhancedColumns = useMemo(() => {
        return baseColumns.map((col) => {
            if (!col.field) return col; // Skip columns without field (like action columns)

            return {
                ...col,
                hide: columnState[col.field]?.hide ?? col.hide ?? false,
            };
        });
    }, [baseColumns, columnState]);

    return {
        columns: enhancedColumns,
        onColumnVisible,
    };
};

export default useColumnState;
